/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import AkFlag from '@atlaskit/flag/flag';
import type { FlagProps } from '@atlaskit/flag/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';

import { type Invitees } from '../../common/types';
import { withLocalIntl } from '../../common/utils/i18n/intl-provider';
import { type Product } from '../../common/utils/products';
import { FlagCompatibleAvatarGroup } from './flag-compatible-avatar-group/main';
import { messages } from './messages';

export type Props = {
	id: FlagProps['id'];
	invitees: Invitees;
	onDismiss?: (id: FlagProps['id']) => void;
	onOpenCoreInvites?: () => void;
	onOptOut?: () => void;
	onSubmit: () => Promise<void>;
	shouldOpenCoreInvites?: boolean;
	targetProduct: Product;
	/** Displayed name of content being invited to (eg. page/issue name). */
	targetTitle: string;
};

export const InvitePromptFlagNoIntl = ({
	invitees,
	targetTitle,
	targetProduct,
	shouldOpenCoreInvites,
	onSubmit,
	onDismiss,
	onOptOut,
	onOpenCoreInvites,
	...props
}: Props): JSX.Element => {
	const firstName = invitees[0].name.split(' ')[0];

	const { formatMessage } = useIntl();

	const allActions = [
		{
			content: formatMessage(messages.addPeopleButton, {
				inviteeCount: invitees.length,
				firstName,
			}),
			onClick: onSubmit,
		},
		{
			content: formatMessage(messages.dismiss, {}),
			onClick: onOptOut,
		},
	];

	return (
		<AkFlag
			{...props}
			testId="invite-prompt"
			icon={
				<FlagCompatibleAvatarGroup
					data={invitees.map((user) => ({
						name: user.name,
						src: user.avatar,
					}))}
					boundariesElement="scrollParent"
				/>
			}
			onDismissed={onDismiss}
			title={formatMessage(messages.titleGeneric, {
				inviteeCount: invitees.length,
				displayName: invitees[0].name,
			})}
			description={
				<Box xcss={mainContentContainerStyles} as="p">
					{targetTitle
						? formatMessage(messages.descriptionWithPageTitleGeneric, {
								strong: (...chunks: React.ReactNode[]) => (
									<Text as="strong">{chunks.join('')}</Text>
								),
								inviteeCount: invitees.length,
								title: targetTitle,
								firstName,
							})
						: formatMessage(
								fg('confluence-issue-terminology-refresh')
									? messages.descriptionWithoutPageTitleGenericIssueTermRefresh
									: messages.descriptionWithoutPageTitleGeneric,
								{
									inviteeCount: invitees.length,
									firstName,
								},
							)}
				</Box>
			}
			actions={onOptOut ? allActions : allActions.slice(0, 1)}
		/>
	);
};

export const InvitePromptFlag: (props: Props) => React.JSX.Element =
	withLocalIntl(InvitePromptFlagNoIntl);

const mainContentContainerStyles = xcss({
	display: '-webkit-box',
	lineClamp: '3',
	boxOrient: 'vertical',
	overflow: 'hidden',
	margin: 'space.0',
});
